import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, LOGO_URL } from "../lib/api";
import { useCart } from "../lib/cart";
import { useI18n } from "../lib/i18n";
import { Check, ArrowLeft } from "lucide-react";

const MAX_POLLS = 10;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { clear } = useCart();
  const { t } = useI18n();
  const [status, setStatus] = useState("loading"); // loading | paid | failed | expired
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setData(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus("failed");
      }
    };
    poll();
    return () => {
      stopped = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div
      data-testid="checkout-success"
      className="min-h-screen bg-black text-white flex items-center justify-center px-6 grain"
    >
      <div className="max-w-md w-full text-center fade-up">
        <img src={LOGO_URL} alt="" className="h-20 w-20 object-contain mx-auto mb-8" />
        {status === "loading" && (
          <>
            <p className="text-xs uppercase tracking-[0.3em] text-green-400 mb-3">
              Verifying payment
            </p>
            <h1 className="font-display text-5xl uppercase tracking-tighter mb-6">
              Processing…
            </h1>
            <div className="w-10 h-10 border-2 border-green-400 border-t-transparent rounded-full animate-spin mx-auto" />
          </>
        )}
        {status === "paid" && (
          <>
            <div className="inline-flex items-center justify-center w-20 h-20 border border-green-400 mb-6">
              <Check size={40} className="text-green-400" />
            </div>
            <p className="text-xs uppercase tracking-[0.3em] text-green-400 mb-3">
              Payment confirmed
            </p>
            <h1 className="font-display text-5xl uppercase tracking-tighter mb-4">
              You're in.
            </h1>
            <p className="text-zinc-400 mb-2">
              Thanks for repping ALLSTAR. ★
            </p>
            {data?.amount_total && (
              <p className="text-zinc-500 text-sm mb-8 uppercase tracking-widest">
                Total: ${(data.amount_total / 100).toFixed(2)} {data.currency?.toUpperCase()}
              </p>
            )}
            <p className="text-xs text-zinc-500 mb-8">
              You'll receive an email confirmation shortly. Order ref:{" "}
              <span className="text-zinc-300">{sessionId?.slice(-12)}</span>
            </p>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <p className="text-xs uppercase tracking-[0.3em] text-red-400 mb-3">
              {status === "expired" ? "Session expired" : "Payment failed"}
            </p>
            <h1 className="font-display text-5xl uppercase tracking-tighter mb-6">
              Something off
            </h1>
            <p className="text-zinc-400 mb-8">
              Don't worry — your cart is still saved. Try again.
            </p>
          </>
        )}
        <button
          onClick={() => navigate("/")}
          data-testid="success-home-btn"
          className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-black font-bold uppercase tracking-widest px-8 py-4 transition-colors"
        >
          <ArrowLeft size={16} /> Back to shop
        </button>
      </div>
    </div>
  );
}
